<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'NAVOnlineInvoiceInterfaceSettings',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'saved']);

  const props = defineProps({
    ...userComponentProps,
    companyId: {
      type: String,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const resource = ref(null);
  const editor = ref(null);
  const formId = ref(null);
  const formName = ref('NAVOnlineInvoiceInterfaceSettings');
  const change_password = ref({
    password: null,
    verify_password: null,
  });
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));

  const $data = computed(() => ({
    resource: resource.value,
    editor: editor.value,
    formId: formId.value,
    formName: formName.value,
    change_password: change_password.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_resource = () => {
    return $fn.value.fetch('read_nav_online_interface_setting', {
      parameters: { company_id: props.companyId },
      asResource: true,
    });
  };
  const $$load_editor = () => {
    return resource.value.editor();
  };
  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'resource' },
      { name: 'editor', dependsOn: ['resource'] },
      { name: 'formId' },
      { name: 'formName' },
      { name: 'change_password' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {
      resource: $$load_resource,
      editor: $$load_editor,
      formId: $$load_formId,
    },
    ux: {},
    dataRefs: {
      resource,
      editor,
      formId,
      formName,
      change_password,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const payload = computed(() => {
    if (loaded.value) {
      return _.extend(
        {},
        editor.value.getData(),
        change_password.value.password && change_password.value.verify_password
          ? change_password.value
          : null
      );
    }
    return null;
  });
  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: [
          $fn.value.pgettext(
            'NAVOnlineInvoiceInterfaceSettings|Title',
            'NAV online invoice interface configuration'
          ),
          resource.value.company_name,
        ],
        icon: $config.value.settings.icon,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.companyId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('companyId');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_wdkrtvzhc_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_script_dig4zc5uz_value_callable = async (
    $event
  ) => {
    return resource.value.reset($event.data.response.data);
  };
  const act_1040167445267876_script_dig4zc5uz_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_1040167445267876_script_dig4zc5uz_value_callable($event);
      },
    };
  };
  const inst_evh_1040167445267876_success_hwcok2iiu_getWhen = async (
    $event
  ) => {
    return !!$event.data.response.data;
  };
  const act_1040167445267876_emit_y106h6bxy_getActionArgs = async ($event) => {
    return {
      event: 'saved',
    };
  };
  const inst_evh_1040167445267876_success_hippt73lp_getWhen = async (
    $event
  ) => {
    return !!$event.data.response.data;
  };
  const act_1040167445267876_request_quejf6kms_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'update_nav_online_interface_setting',
      data: payload.value,
    };
  };
  const act_2434514588044777_closeModal_ld312q1hz_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
    };
  };

  const act_7315092382398562_closeModal_wdkrtvzhc =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_wdkrtvzhc_getActionArgs,
    });
  const inst_evh_7315092382398562_close_803z0oyjf =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_wdkrtvzhc,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_803z0oyjf],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_s1x4kkhrx =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_laoyjz7vc =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_s1x4kkhrx,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_laoyjz7vc],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_28xggql8d =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_4h09eswvo =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_28xggql8d,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_4h09eswvo],
    event: 'close',
  });
  const act_1040167445267876_script_dig4zc5uz = new actionTypes.ScriptAction({
    actionArgs: act_1040167445267876_script_dig4zc5uz_getActionArgs,
  });
  const inst_evh_1040167445267876_success_hwcok2iiu =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_script_dig4zc5uz,
      event: 'success',
      when: inst_evh_1040167445267876_success_hwcok2iiu_getWhen,
      displayName: 'script',
    });
  const act_1040167445267876_emit_y106h6bxy = new actionTypes.EmitAction({
    actionArgs: act_1040167445267876_emit_y106h6bxy_getActionArgs,
  });
  const inst_evh_1040167445267876_success_hippt73lp =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_emit_y106h6bxy,
      event: 'success',
      when: inst_evh_1040167445267876_success_hippt73lp_getWhen,
      displayName: 'emit',
    });
  const act_1040167445267876_request_quejf6kms = new actionTypes.RequestAction({
    actionArgs: act_1040167445267876_request_quejf6kms_getActionArgs,
    displayName: 'submit',
    events: [
      inst_evh_1040167445267876_success_hwcok2iiu,
      inst_evh_1040167445267876_success_hippt73lp,
    ],
  });
  const inst_evh_1040167445267876_submit_p6yxgz7gs =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_request_quejf6kms,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_p6yxgz7gs],
    event: 'submit',
  });
  const act_2434514588044777_closeModal_ld312q1hz =
    new actionTypes.CloseModalAction({
      actionArgs: act_2434514588044777_closeModal_ld312q1hz_getActionArgs,
    });
  const inst_evh_2434514588044777_clickPreventStop_1su0nxnx4 =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeModal_ld312q1hz,
      event: 'click.prevent.stop',
      displayName: 'closeModal',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_1su0nxnx4],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_803z0oyjf,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_laoyjz7vc,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_4h09eswvo,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_hwcok2iiu,
    inst_evh_1040167445267876_success_hippt73lp,
    inst_evh_1040167445267876_submit_p6yxgz7gs,
    submit: inst_evh_1040167445267876_submit_p6yxgz7gs,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_1su0nxnx4,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_803z0oyjf = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_803z0oyjf.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_laoyjz7vc = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_laoyjz7vc.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_4h09eswvo = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_4h09eswvo.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_hwcok2iiu = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_hwcok2iiu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_hippt73lp = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_hippt73lp.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_p6yxgz7gs = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_p6yxgz7gs.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_1su0nxnx4 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_1su0nxnx4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="NAVOnlineInvoiceInterfaceSettings"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :name="formName"
        :readonly="resource.locked"
        :labelAlign="$config.forms.defaultLabelAlign"
        :id="formId"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpRow>
          <ItpCol>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="company_name"
              :label="
                pgettext('NAVOnlineInvoiceInterfaceSettings', 'Company name')
              "
              required
            >
              <BFormInput
                plaintext
                v-model="editor.company_name"
                class="fw-bold no-focus-border"
              >
              </BFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="base_url"
              :label="pgettext('NAVOnlineInvoiceInterfaceSettings', 'Base URL')"
              :helpTooltip="
                pgettext(
                  'NAVOnlineInvoiceInterfaceSettings',
                  'The root URL of the service.'
                )
              "
              required
            >
              <ItpFormInput name="base_url" v-model="editor.base_url" required>
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="tax_number"
              :label="
                pgettext('NAVOnlineInvoiceInterfaceSettings', 'Tax number')
              "
              required
              :helpTooltip="
                pgettext(
                  'NAVOnlineInvoiceInterfaceSettings',
                  'The first 8 digits of the tax number.'
                )
              "
            >
              <ItpFormInput
                name="tax_number"
                v-model="editor.tax_number"
                required
              >
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="login"
              :label="pgettext('NAVOnlineInvoiceInterfaceSettings', 'Login')"
              :helpTooltip="
                pgettext(
                  'NAVOnlineInvoiceInterfaceSettings',
                  'The technical user\'s login name.'
                )
              "
              required
            >
              <ItpFormInput name="login" v-model="editor.login" required>
              </ItpFormInput>
            </ItpFormGroup>
            <hr />
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="password_hash"
              :label="
                pgettext('NAVOnlineInvoiceInterfaceSettings', 'Password hash')
              "
              :helpTooltip="
                pgettext(
                  'NAVOnlineInvoiceInterfaceSettings',
                  'The generated password\'s SHA-512 hash.'
                )
              "
            >
              <ItpFormTextarea
                name="password_hash"
                v-model="resource.password"
                readonly
              >
              </ItpFormTextarea>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="password"
              :label="pgettext('NAVOnlineInvoiceInterfaceSettings', 'Password')"
              :helpTooltip="
                pgettext(
                  'NAVOnlineInvoiceInterfaceSettings',
                  'The generated password.'
                )
              "
            >
              <ItpFormInput
                type="password"
                name="password"
                v-model="change_password.password"
              >
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="password"
              :label="
                pgettext(
                  'NAVOnlineInvoiceInterfaceSettings',
                  'Confirm password'
                )
              "
              :helpTooltip="
                pgettext(
                  'NAVOnlineInvoiceInterfaceSettings',
                  'The generated password.'
                )
              "
            >
              <ItpFormInput
                type="password"
                name="verify_password"
                v-model="change_password.verify_password"
              >
              </ItpFormInput>
            </ItpFormGroup>
            <hr />
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="xml_sign_key"
              :label="
                pgettext('NAVOnlineInvoiceInterfaceSettings', 'XML sign key')
              "
              :helpTooltip="
                pgettext(
                  'NAVOnlineInvoiceInterfaceSettings',
                  'The technical user\'s XML sign key.'
                )
              "
              required
            >
              <ItpFormInput
                name="xml_sign_key"
                v-model="editor.xml_sign_key"
                required
              >
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="exchange_key"
              :label="
                pgettext(
                  'NAVOnlineInvoiceInterfaceSettings',
                  'XML exchange key'
                )
              "
              :helpTooltip="
                pgettext(
                  'NAVOnlineInvoiceInterfaceSettings',
                  'The technical user\'s XML exchange key.'
                )
              "
              required
            >
              <ItpFormInput
                name="exchange_key"
                v-model="editor.exchange_key"
                required
              >
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="add_unknown_clients"
              :label="
                pgettext(
                  'NAVOnlineInvoiceInterfaceSettings',
                  'Save unknown clients.'
                )
              "
              :helpTooltip="
                pgettext(
                  'NAVOnlineInvoiceInterfaceSettings',
                  'Add unknown client to the database.'
                )
              "
            >
              <ItpFormCheckbox
                name="add_unknown_clients"
                v-model="editor.add_unknown_clients"
              >
              </ItpFormCheckbox>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="2"
              name="lookback_period"
              :label="
                pgettext('NAVOnlineInvoiceInterfaceSettings', 'Lookback period')
              "
              :helpTooltip="
                pgettext(
                  'NAVOnlineInvoiceInterfaceSettings',
                  'The number of days to be used to look back for invoices.'
                )
              "
              contentClass="input-group"
            >
              <ItpFormInput
                type="number"
                name="lookback_period"
                v-model="editor.lookback_period"
              >
              </ItpFormInput>
              <span class="input-group-text">
                {{ pgettext('InputGroupAddon', 'days') }}
              </span>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="2"
              name="request_timeout"
              :label="
                pgettext('NAVOnlineInvoiceInterfaceSettings', 'Request timeout')
              "
              :helpTooltip="
                pgettext(
                  'NAVOnlineInvoiceInterfaceSettings',
                  'Stop waiting for a response once the specified timeout has expired.'
                )
              "
              contentClass="input-group"
            >
              <ItpFormInput
                type="number"
                name="request_timeout"
                v-model="editor.request_timeout"
              >
              </ItpFormInput>
              <span class="input-group-text">
                {{ gettext('sec') }}
              </span>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="auto_sync"
              :label="
                pgettext('NAVOnlineInvoiceInterfaceSettings', 'Auto sync')
              "
              :helpTooltip="
                pgettext(
                  'NAVOnlineInvoiceInterfaceSettings',
                  'Check to include this company to the automated sync job.'
                )
              "
            >
              <ItpFormCheckbox name="auto_sync" v-model="editor.auto_sync">
              </ItpFormCheckbox>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="pull_incoming_invoices"
              :label="
                pgettext(
                  'NAVOnlineInvoiceInterfaceSettings',
                  'Pull incoming invoices'
                )
              "
              :helpTooltip="
                pgettext(
                  'NAVOnlineInvoiceInterfaceSettings',
                  'Enable pulling of incoming invoices from the agency.'
                )
              "
            >
              <ItpFormCheckbox
                name="pull_incoming_invoices"
                v-model="editor.pull_incoming_invoices"
              >
              </ItpFormCheckbox>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="pull_outgoing_invoices"
              :label="
                pgettext(
                  'NAVOnlineInvoiceInterfaceSettings',
                  'Pull outgoing invoices'
                )
              "
              :helpTooltip="
                pgettext(
                  'NAVOnlineInvoiceInterfaceSettings',
                  'Enable pulling of outgoing invoices from the agency.'
                )
              "
            >
              <ItpFormCheckbox
                name="pull_outgoing_invoices"
                v-model="editor.pull_outgoing_invoices"
              >
              </ItpFormCheckbox>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="invoice_tag"
              :label="
                pgettext('NAVOnlineInvoiceInterfaceSettings', 'Invoice tag')
              "
              :helpTooltip="
                pgettext(
                  'NAVOnlineInvoiceInterfaceSettings',
                  'The tag to set on the imported invoices.'
                )
              "
            >
              <ItpFormInput name="invoice_tag" v-model="editor.invoice_tag">
              </ItpFormInput>
            </ItpFormGroup>
          </ItpCol>
        </ItpRow>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :form="forms[formName] && forms[formName].id"
          :text="pgettext('Button', 'Save')"
          type="submit"
          variant="primary"
          default
          :disabled="!forms[formName] || !forms[formName].submittable"
          :spinning="actions.submit && actions.submit.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="pgettext('Button', 'Close')"
          variant="default"
          :disabled="actions.submit && actions.submit.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
